import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { borneUrl } from '../lib/borneUrl';
import L from 'leaflet';
import { getAntennesStats, getContacts, getBornes, setAuthToken, getUploadsUrl, getVilles, getQualifications } from '../services/api';
import MultiSelect from '../components/MultiSelect';
import { attachSearchBox, isPlacesEnabled, getApiKeyHint } from '../lib/googlePlaces';
import './Carte.css';
import {
  QUALIFICATION_COLORS,
  QUALIFICATION_LABELS,
  BORNE_TYPE_COLORS,
  BORNE_TYPE_COLOR_MAP,
  getCachedIcon,
  createRepereRechercheIcon,
} from '../lib/carteMarkers';

function MapResizeHandler({ expanded }) {
  const map = useMap();
  useEffect(() => {
    const timer = setTimeout(() => { map.invalidateSize(); }, 350);
    return () => clearTimeout(timer);
  }, [expanded, map]);
  return null;
}

// Recentre la carte sur `target` ({lat, lng, bounds?}).
//
// Deux cas, et c'est ce qui rend la recherche precise : quand Google donne
// l'etendue du lieu, on cadre dessus — une ville se voit en entier, un numero
// de rue se colle a la porte. Sans etendue, on prend un zoom d'adresse (16),
// pas un zoom de ville : chercher une adresse et arriver a 13 laissait
// l'impression que la carte n'avait pas trouve.
function MapFlyTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (!target || !Number.isFinite(target.lat) || !Number.isFinite(target.lng)) return;
    if (target.bounds) {
      // `maxZoom` : sans lui, l'etendue minuscule d'un commerce cadre au ras
      // du toit et on perd tout repere alentour.
      map.flyToBounds(target.bounds, { maxZoom: 17, duration: 0.8, padding: [40, 40] });
    } else {
      map.flyTo([target.lat, target.lng], target.zoom || 16, { duration: 0.8 });
    }
  }, [target, map]);
  return null;
}

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// ── Marker design ──
// 0 borne   : teardrop simple, couleur = qualification, ⭐ central si prio
// 1 type    : teardrop fond = couleur du type, bordure = qualification,
//             nombre de bornes au centre
// 2+ types  : teardrop fond = qualification, nombre TOTAL de bornes au centre
//             (le détail par type reste visible dans la popup)


function Carte() {
  const auth = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [allBornes, setAllBornes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapCenter] = useState([47.720348, -0.866559]);
  const [mapExpanded, setMapExpanded] = useState(false);
  const [selectedMarkerId, setSelectedMarkerId] = useState(null);

  // Lookups
  const [villesLookup, setVillesLookup] = useState([]);
  const [qualificationsLookup, setQualificationsLookup] = useState([]);

  // Filtres — par défaut on affiche uniquement les "Antennes actuelles"
  const [search, setSearch] = useState('');
  const [selectedVilles, setSelectedVilles] = useState([]);
  const [selectedQualifications, setSelectedQualifications] = useState(['antenne_principale']);
  const [selectedTypeBornes, setSelectedTypeBornes] = useState([]);
  const [appliedTypeBornes, setAppliedTypeBornes] = useState([]);
  const [selectedEtat, setSelectedEtat] = useState('');
  const [selectedPrioritaire, setSelectedPrioritaire] = useState(false);

  // Google Places autocomplete (SearchBox style — comme le CRM PROD)
  const placesEnabled = isPlacesEnabled();
  const [flyToTarget, setFlyToTarget] = useState(null);
  // Le lieu recherche, materialise sur la carte. Sans lui, on ne voyait que la
  // zone : « c'est par la » plutot que « c'est ici ».
  const [repere, setRepere] = useState(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    console.info(`[Carte] Google Places ${placesEnabled ? 'ENABLED' : 'DISABLED'} (${getApiKeyHint()})`);
    if (!placesEnabled || !searchInputRef.current) return;
    let cleanup = null;
    attachSearchBox(searchInputRef.current, ({ lat, lng, name, address, bounds }) => {
      setSearch(name || '');
      setFlyToTarget({ lat, lng, bounds });
      setRepere({ lat, lng, name, address });
    }).then((fn) => { cleanup = fn; });
    return () => { if (cleanup) cleanup(); };
  }, [placesEnabled]);

  useEffect(() => {
    if (auth.user?.access_token) {
      setAuthToken(auth.user.access_token);
      // Charge avec le filtre par défaut "antenne_principale"
      loadData({ qualifications: 'antenne_principale' });
      loadLookups();
    }
  }, [auth.user]);

  const loadLookups = async () => {
    try {
      const [villesData, qualData] = await Promise.all([getVilles(), getQualifications()]);
      setVillesLookup(villesData);
      setQualificationsLookup(qualData);
    } catch (error) {
      console.error('Error loading lookups:', error);
    }
  };

  const loadData = async (params = {}) => {
    try {
      setLoading(true);
      const contactParams = { limit: 1000, ...params };
      const [statsData, contactsData, bornesData] = await Promise.all([
        getAntennesStats(),
        getContacts(contactParams),
        getBornes().catch(() => [])
      ]);
      setStats(statsData);
      const contactsWithCoords = (contactsData.data || []).filter(
        c => c.addr_lat && c.addr_lng && !isNaN(parseFloat(c.addr_lat)) && !isNaN(parseFloat(c.addr_lng))
      );
      setContacts(contactsWithCoords);
      setAllBornes(bornesData || []);
    } catch (error) {
      console.error('Error loading map data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setAppliedTypeBornes([...selectedTypeBornes]);
    const params = {};
    if (search) params.search = search;
    if (selectedVilles.length > 0) params.villes = selectedVilles.join(',');
    if (selectedQualifications.length > 0) params.qualifications = selectedQualifications.join(',');
    if (selectedEtat) params.etat = selectedEtat;
    if (selectedPrioritaire) params.prioritaire = 'true';
    loadData(params);
  };

  const clearFilters = () => {
    setSearch('');
    setRepere(null);
    setSelectedVilles([]);
    // Conserve la qualification "Antenne actuelle" par defaut (pas vide)
    // pour rester aligne sur le comportement par defaut de la page.
    setSelectedQualifications(['antenne_principale']);
    setSelectedTypeBornes([]);
    setAppliedTypeBornes([]);
    setSelectedEtat('');
    setSelectedPrioritaire(false);
    loadData({ qualifications: 'antenne_principale' });
  };

  // Mapping contact_id -> bornes (direct assignment only, via contact_antenne_id)
  const bornesByContact = useMemo(() => {
    const map = {};
    allBornes.forEach(b => {
      if (b.contact_antenne_id) {
        if (!map[b.contact_antenne_id]) map[b.contact_antenne_id] = [];
        map[b.contact_antenne_id].push(b);
      }
    });
    return map;
  }, [allBornes]);

  const getContactBornes = (contact) => {
    return bornesByContact[contact.id] || [];
  };

  // Client-side filtering for borne types only
  const filteredContacts = useMemo(() => {
    if (appliedTypeBornes.length === 0) return contacts;
    return contacts.filter(c => {
      const bornes = getContactBornes(c);
      return appliedTypeBornes.some(type => bornes.some(b => b.type_borne === type));
    });
  }, [contacts, appliedTypeBornes, bornesByContact]);

  const hasActiveFilters = search || selectedVilles.length > 0 || selectedQualifications.length > 0 || selectedTypeBornes.length > 0 || selectedEtat || selectedPrioritaire;

  const villeOptions = villesLookup.map(v => ({ value: v, label: v }));
  const qualificationOptions = qualificationsLookup.map(q => ({ value: q.value, label: q.label }));

  const getQualificationKey = (contact) => {
    const q = contact.qualification;
    if (!q || q === '') return 'nouveau';
    if (q === 'profil_intéressant' || q === 'profil_interessant') return 'profil_intéressant';
    return q;
  };

  // Compte les bornes par type pour un contact (classik / spherik / ...)
  const buildTypesCounts = (contact) => {
    const counts = { classik: 0, spherik: 0, prestige: 0, accessoire: 0 };
    (getContactBornes(contact) || []).forEach((b) => {
      const t = b.type_borne;
      if (t && counts[t] !== undefined) counts[t] += 1;
    });
    return counts;
  };

  // Mode d'affichage des marqueurs (URL ?mode=N + dropdown UI)
  // 1 = détaillé — carte blanche avec compteurs par type
  // 2 = épuré — teardrop simple + petits points colorés au-dessus
  // 3 = camembert — types remplissent le marqueur, contour qualif épais
  const markerMode = (() => {
    const m = parseInt(searchParams.get('mode'), 10);
    return m === 2 || m === 3 ? m : 1;
  })();
  const [modeMenuOpen, setModeMenuOpen] = useState(false);
  const modeMenuRef = useRef(null);
  useEffect(() => {
    if (!modeMenuOpen) return undefined;
    const onClick = (e) => {
      if (modeMenuRef.current && !modeMenuRef.current.contains(e.target)) setModeMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [modeMenuOpen]);
  const setMarkerMode = (m) => {
    const next = new URLSearchParams(searchParams);
    if (m === 1) next.delete('mode'); else next.set('mode', String(m));
    setSearchParams(next, { replace: true });
    setModeMenuOpen(false);
  };

  const getMarkerIcon = (contact, isSelected = false) => {
    const qualKey = getQualificationKey(contact);
    const qualifColor = QUALIFICATION_COLORS[qualKey] || QUALIFICATION_COLORS['nouveau'];
    const isPrio = !!contact.prioritaire;
    const typesCounts = buildTypesCounts(contact);
    return getCachedIcon(markerMode, qualifColor, typesCounts, isSelected, isPrio);
  };

  return (
    <div id="carte" className="carte-page">
      <header className="page-header">
        <div>
          <h1>Carte</h1>
          <p className="page-subtitle">Localisation des antennes</p>
        </div>
      </header>

      {/* Barre de filtres */}
      <form onSubmit={handleSearch} className="carte-filter-bar">
        <div className="search-input-wrapper">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Rechercher (nom, ville, adresse...)"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              // Le champ vide : le repere n'a plus de lieu a designer.
              if (!e.target.value.trim()) setRepere(null);
            }}
            className="search-input"
            autoComplete="off"
          />
        </div>

        <MultiSelect
          options={villeOptions}
          selected={selectedVilles}
          onChange={setSelectedVilles}
          placeholder="Villes principales"
          searchable
        />

        <MultiSelect
          options={qualificationOptions}
          selected={selectedQualifications}
          onChange={setSelectedQualifications}
          placeholder="Qualification"
          colorMap={QUALIFICATION_COLORS}
        />

        <MultiSelect
          options={[
            { value: 'classik', label: 'Classik' },
            { value: 'spherik', label: 'Spherik' },
            { value: 'prestige', label: 'Prestige' },
            { value: 'accessoire', label: 'Accessoire' },
          ]}
          selected={selectedTypeBornes}
          onChange={setSelectedTypeBornes}
          placeholder="Type de borne"
          colorMap={BORNE_TYPE_COLOR_MAP}
        />

        <select
          value={selectedEtat}
          onChange={e => setSelectedEtat(e.target.value)}
          className="filter-badge-select"
        >
          <option value="">État</option>
          <option value="actif">Actif</option>
          <option value="inactif">Inactif</option>
        </select>

        {/* Toggle Prioritaires uniquement */}
        <label
          title="Afficher uniquement les contacts prioritaires"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '7px 12px', borderRadius: 8,
            border: `1px solid ${selectedPrioritaire ? '#f59e0b' : '#e2e8f0'}`,
            background: selectedPrioritaire ? 'linear-gradient(135deg, #fef3c7, #fde68a)' : 'white',
            color: selectedPrioritaire ? '#92400e' : '#475569',
            fontSize: 12, fontWeight: 600, cursor: 'pointer',
            userSelect: 'none', whiteSpace: 'nowrap',
            transition: 'all 0.15s ease',
          }}
        >
          <input
            type="checkbox"
            checked={selectedPrioritaire}
            onChange={(e) => setSelectedPrioritaire(e.target.checked)}
            style={{ display: 'none' }}
          />
          <span style={{ fontSize: 13 }}>{selectedPrioritaire ? '⭐' : '☆'}</span>
          Prioritaires
        </label>

        <button type="submit" className="btn-search" title="Rechercher">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </button>

        <button type="button" onClick={clearFilters} className="btn-reset" title="Réinitialiser les filtres">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="1 4 1 10 7 10" />
            <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
          </svg>
        </button>

      </form>

      <section className={`carte-map-card ${mapExpanded ? 'map-expanded' : ''}`}>
        <div className="card-header-gradient card-header-gradient-map">
          <div className="card-header-left">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            <h2>Carte des antennes</h2>
          </div>
          <div className="card-header-actions">
            <span className="carte-filter-count">
              {filteredContacts.length} / {contacts.length} contacts
            </span>
            <span className="card-header-meta">
              {stats?.total_antennes || 0} antennes &bull; {stats?.total_villes || 0} villes
            </span>

            {/* Dropdown "Mode d'affichage" — sélection mode 1/2/3 */}
            <div className="map-mode-wrap" ref={modeMenuRef}>
              <button
                type="button"
                className="map-mode-btn"
                onClick={() => setModeMenuOpen((v) => !v)}
                title="Choisir le mode d'affichage des marqueurs"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 3 V12 L20 16" />
                </svg>
                Mode {markerMode}
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {modeMenuOpen && (
                <div className="map-mode-panel">
                  <button
                    type="button"
                    className={`map-mode-option${markerMode === 1 ? ' active' : ''}`}
                    onClick={() => setMarkerMode(1)}
                  >
                    <span className="map-mode-num">1</span>
                    <span>
                      <strong>Détaillé</strong>
                      <em>Carte avec compteurs par type</em>
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`map-mode-option${markerMode === 2 ? ' active' : ''}`}
                    onClick={() => setMarkerMode(2)}
                  >
                    <span className="map-mode-num">2</span>
                    <span>
                      <strong>Épuré</strong>
                      <em>Teardrop + points colorés</em>
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`map-mode-option${markerMode === 3 ? ' active' : ''}`}
                    onClick={() => setMarkerMode(3)}
                  >
                    <span className="map-mode-num">3</span>
                    <span>
                      <strong>Camembert</strong>
                      <em>Types remplissent le marqueur</em>
                    </span>
                  </button>
                </div>
              )}
            </div>

            <button
              className="map-expand-btn"
              onClick={() => setMapExpanded(!mapExpanded)}
              title={mapExpanded ? 'Réduire la carte' : 'Agrandir la carte'}
            >
              {mapExpanded ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="4 14 10 14 10 20" />
                  <polyline points="20 10 14 10 14 4" />
                  <line x1="14" y1="10" x2="21" y2="3" />
                  <line x1="3" y1="21" x2="10" y2="14" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 3 21 3 21 9" />
                  <polyline points="9 21 3 21 3 15" />
                  <line x1="21" y1="3" x2="14" y2="10" />
                  <line x1="3" y1="21" x2="10" y2="14" />
                </svg>
              )}
              {mapExpanded ? 'Réduire' : 'Plein écran'}
            </button>
          </div>
        </div>
        <div className="carte-map-container">
          {loading ? (
            <div className="map-loading">
              <div className="spinner"></div>
              <p>Chargement de la carte...</p>
            </div>
          ) : filteredContacts.length > 0 || repere ? (
            <MapContainer center={mapCenter} zoom={7} scrollWheelZoom={true} className="carte-leaflet-map">
              <MapResizeHandler expanded={mapExpanded} />
              <MapFlyTo target={flyToTarget} />
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {/* Le lieu recherche. Rendu AVANT les antennes pour passer
                  dessous : si une antenne est a la meme adresse, c'est elle
                  qu'on veut pouvoir cliquer. */}
              {repere && (
                <Marker
                  position={[repere.lat, repere.lng]}
                  icon={createRepereRechercheIcon()}
                  zIndexOffset={-500}
                >
                  {/* Bulle propre au repere : elle ne reprend PAS `contact-popup`,
                      qui impose `min-width: 340px` et une mise en page pensee pour
                      une fiche (bandeau de qualification, photo, bornes). Ici il n'y
                      a qu'un nom et une adresse. */}
                  {/* La croix de Leaflet est conservee : elle REFERME la bulle, la ou
                      « Retirer » EFFACE le repere. Confondre les deux ferait
                      perdre le point a qui voulait juste masquer l’infobulle. */}
                  <Popup className="repere-popup" minWidth={230} maxWidth={310}>
                    <div className="repere-bulle">
                      <p className="repere-bulle-nom">{repere.name || 'Lieu recherché'}</p>
                      {repere.address && <p className="repere-bulle-adresse">{repere.address}</p>}
                      <button
                        type="button"
                        className="repere-bulle-retirer"
                        onClick={() => setRepere(null)}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                        Retirer ce repère
                      </button>
                    </div>
                  </Popup>
                </Marker>
              )}

              {filteredContacts.map((contact) => {
                const contactBornes = getContactBornes(contact);
                return (
                  <Marker
                    key={contact.id}
                    position={[parseFloat(contact.addr_lat), parseFloat(contact.addr_lng)]}
                    icon={getMarkerIcon(contact, selectedMarkerId === contact.id)}
                    eventHandlers={{
                      popupopen: () => setSelectedMarkerId(contact.id),
                      popupclose: () => setSelectedMarkerId(null),
                    }}
                  >
                    <Popup className="contact-popup">
                      <div className="popup-content">
                        <div className="popup-qual-banner" style={{ background: QUALIFICATION_COLORS[getQualificationKey(contact)] || QUALIFICATION_COLORS['nouveau'] }}>
                          {QUALIFICATION_LABELS[getQualificationKey(contact)] || 'Nouveau'}
                        </div>
                        <div className="popup-body">
                          {contact.photo && (
                            <div className="popup-photo">
                              <img src={getUploadsUrl(contact.photo)} alt={`${contact.prenom} ${contact.nom}`} />
                            </div>
                          )}
                          <div className="popup-info">
                            {(() => {
                              const fullName = `${contact.prenom || ''} ${contact.nom || ''}`.trim();
                              const enseigne = contact.enseigne || contact.raison_sociale || '';
                              const titleText = fullName || enseigne || 'Contact';
                              return (
                                <>
                                  <h4>
                                    {contact.prioritaire && <span style={{ marginRight: 6 }}>⭐</span>}
                                    <a
                                      href={`/contacts/${contact.id}`}
                                      style={{ color: '#1d4ed8', textDecoration: 'none', fontWeight: 600 }}
                                      onMouseOver={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                                      onMouseOut={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                                    >
                                      {titleText}
                                    </a>
                                  </h4>
                                  {fullName && enseigne && (
                                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, fontWeight: 500 }}>{enseigne}</div>
                                  )}
                                </>
                              );
                            })()}
                            {contactBornes.length > 0 && (
                              <div className="popup-bornes-simple">
                                <div className="popup-bornes-types">
                                  {Object.entries(
                                    contactBornes.reduce((acc, b) => {
                                      const type = b.type_borne || 'autre';
                                      acc[type] = (acc[type] || 0) + 1;
                                      return acc;
                                    }, {})
                                  ).map(([type, count]) => (
                                    <span key={type} className="popup-borne-type-badge" style={{
                                      background: BORNE_TYPE_COLORS[type]?.bg || '#f8fafc',
                                      color: BORNE_TYPE_COLORS[type]?.color || '#475569'
                                    }}>
                                      {count} {BORNE_TYPE_COLORS[type]?.label || type}
                                    </span>
                                  ))}
                                </div>
                                <div className="popup-bornes-nums">
                                  <strong>{contactBornes.length > 1 ? 'Bornes' : 'Borne'} : </strong>
                                  {contactBornes.map((b, idx) => {
                                    const href = borneUrl(b);
                                    return (
                                      <span key={b.id || b.numero_formated}>
                                        {idx > 0 && ', '}
                                        {href ? (
                                          <a
                                            href={href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            title="Ouvrir la fiche borne"
                                            style={{ color: BORNE_TYPE_COLOR_MAP[b.type_borne] || '#475569', fontWeight: 600, textDecoration: 'underline' }}
                                          >
                                            {b.numero_formated}
                                          </a>
                                        ) : (
                                          b.numero_formated
                                        )}
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            {(contact.ville || contact.adresse) && (
                              <p className="popup-address">
                                {contact.adresse && `${contact.adresse}, `}
                                {contact.cp && `${contact.cp} `}
                                {contact.ville}
                              </p>
                            )}
                          </div>
                        </div>
                        <Link to={`/contacts/${contact.id}`} className="popup-link">Voir le profil →</Link>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          ) : (
            <div className="map-empty">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              <p>{hasActiveFilters ? 'Aucun résultat avec ces filtres' : 'Aucune antenne géolocalisée'}</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default Carte;
